/**
 * DBUWI(13) = TS(3)M(1)RG(2)LSD(2)SEC(2)LE(2)ES(1)
 * PrettyUWI(19) = LE(2)/LSD(2)-SEC(2)-TS(3)-RG(2)WM(1)/0ES(1)
 * FullUWI(20) =  DLS(1)LE(2)/LSD(2)-SEC(2)-TS(3)-RG(2)WM(1)/0ES(1)
 *
 * LE/DD-SS-TTT-RRWM/ES
 * DLS: (1) -> Dominion Land Survey. Always a 1 for Canada.
 * LE:  (?!01)([0-9A-Z](?![IO])[0-9A-Z]) -> Local Exception; 2 characters in length, cannot end with a I or O. Cannot be 01.
 *      Can be 3 characters long including DLS
 * LSD: (0[1-9]|1[0-6]) -> Legal Sub Division; 2 digit code between 1 and 16. Leading zero required.
 * SEC: (0[1-9]|1[0-9]|2[0-9]|3[0-6]) -> Section; 2 digit code between 1 and 36. Leading zero required.
 * TS:  (0{2}[1-9]|(0{1}[1-9][0-9])|1[0-1][0-9]|12[0-6]) -> Township; 3 digit code between 1 and 126. Leading zero required.
 * RG:  (0{1}[1-9]|[1-2][0-9]|30) -> Range; 2 digit code between 1 and 30. Leading zero required.
 * M:   [456] -> Meridian; 1 digit code. Either 4,5 or 6
 * ES:  ((0[2-9]|[1-9][0-9])|(?!1)[0-9])) -> Event Sequence; 1 digit, 0 to 9 and not 1. Or 2 digits 00 to 99, cannot be 01.
 *      Can be 3 characters long
 */

export const UWI_REGEX =
  /^([\w\d]{2,3})\/([\d]{2})-([\d]{2})-([\d]{3})-([\d]{2})W([\d]{1})\/([\d]{1,3})$/

export interface UWI {
  DLS?: string
  LE: string
  LSD: string
  SEC: string
  TS: string
  RG: string
  M: string
  ES: string
}

/**
 * Destruct the UWI string into individual UWI fields for easy manipulation.
 *
 * @param strUwi String UWI
 * @returns UWI object
 */
export const toUWI = (strUwi: string): UWI => {
  const uwi: string[] =
    strUwi && strUwi.trim().length > 0 ? strUwi.toUpperCase().match(UWI_REGEX) : []
  if (uwi == null || uwi.length !== 8) {
    return undefined
  }
  let dls = undefined
  let le = uwi[1]
  if (le.length === 3) {
    dls = le.substring(0, 1)
    le = le.substring(1)
  }
  return {
    DLS: dls,
    LE: le,
    LSD: uwi[2],
    SEC: uwi[3],
    TS: uwi[4],
    RG: uwi[5],
    M: uwi[6],
    ES: uwi[7],
  }
}

/**
 * Convert Pretty UWI to DB UWI
 *
 * @param uwi Pretty UWI
 * @returns DB UWI
 */
export function convertUWI(uwi: string) {
  const components = toUWI(uwi)
  if (components == undefined) {
    return undefined
  }
  const prettyUWI: string =
    components.TS +
    components.M +
    components.RG +
    components.SEC +
    components.LSD +
    components.LE +
    components.ES
  return prettyUWI
}

/**
 * Convert DB UWI to Pretty UWI
 *
 * @param dbUwi DB UWI
 * @returns
 */
export function prettifyUWI(dbUwi: string) {
  return (
    dbUwi[10] +
    dbUwi[11] +
    '/' +
    dbUwi[8] +
    dbUwi[9] +
    '-' +
    dbUwi[6] +
    dbUwi[7] +
    '-' +
    dbUwi[0] +
    dbUwi[1] +
    dbUwi[2] +
    '-' +
    dbUwi[4] +
    dbUwi[5] +
    'W' +
    dbUwi[3] +
    '/' +
    dbUwi[12]
  )
}

export const validateUWI = (strUwi: string): boolean => {
  return validateUwiStatus(strUwi) === UwiValidationStatus.OK
}

export enum UwiValidationStatus {
  OK,
  INVALID_FORMAT,
  INVALID_DLS,
  INVALID_LE,
  INVALID_LSD,
  INVALID_SEC,
  INVALID_TS,
  INVALID_RG,
  INVALID_M,
  INVALID_ES,
}

export const validateUwiStatus = (strUwi: string): UwiValidationStatus => {
  const uwi = toUWI(strUwi)
  if (uwi == undefined) {
    return UwiValidationStatus.INVALID_FORMAT
  }

  const { DLS, LE, LSD, SEC, TS, RG, M, ES } = uwi

  // DLS validation
  if (DLS != null && DLS !== '1') {
    return UwiValidationStatus.INVALID_DLS
  }

  // LE validation
  if (LE.length !== 2 || LE.charAt(1) === 'I' || LE.charAt(1) === 'O' || LE === '01') {
    return UwiValidationStatus.INVALID_LE
  }

  // LSD validation
  if (LSD.length !== 2 || !isBetween(LSD, 1, 16)) {
    return UwiValidationStatus.INVALID_LSD
  }

  // SEC validation
  if (SEC.length !== 2 || !isBetween(SEC, 1, 36)) {
    return UwiValidationStatus.INVALID_SEC
  }

  // TS validation
  if (TS.length !== 3 || !isBetween(TS, 1, 126)) {
    return UwiValidationStatus.INVALID_TS
  }

  // RG validation
  if (RG.length !== 2 || !isBetween(RG, 1, 30)) {
    return UwiValidationStatus.INVALID_RG
  }

  // M validation
  if (M.length !== 1 || !isBetween(M, 4, 6)) {
    return UwiValidationStatus.INVALID_M
  }

  // ES validation
  if (!isBetween(ES.length, 1, 3) || parseInt(ES) === 1) {
    return UwiValidationStatus.INVALID_ES
  }

  return UwiValidationStatus.OK
}

export const validateUwiString = (strUwi: string): string => {
  const result = validateUwiStatus(strUwi)
  if (result !== UwiValidationStatus.OK) {
    if (result === UwiValidationStatus.INVALID_FORMAT) {
      return 'Invalid UWI Format. Format has to be 00/00-00-000-00W0/0'
    } else {
      return 'Invalid UWI'
    }
  }
  return undefined
}

const isBetween = (num: string | number, min: number, max: number): boolean => {
  if (typeof num === 'string') {
    num = parseInt(num, 10)
  }
  if (typeof num !== 'number' || Number.isNaN(num)) {
    return false
  }
  return num >= min && num <= max
}
